// Parameter form wiring.
//
// All form HTML comes from the Rust side (viewer::form_html); this module only
// wires generic events to anything carrying a data-param attribute, plus the
// compound-widget special cases (base-size, unit-slider, seed). `wireParams`
// is the reusable core, driving any `ParamsForm`/`StructForm`-shaped object
// (`set_param`, `param_names`, `param_values`). `Form` wraps a single
// `ParamsForm` pinned to the movement tray for the standalone mode.
import { FormKind, ParamsForm } from "../pkg/viewer.js";

// The subset of ParamsForm / StructForm the generic wiring needs. The wasm
// bindings return an Array<string> of names and a Float64Array of values.
export interface ParamHost {
  set_param(name: string, value: number): boolean;
  param_names(): string[];
  param_values(): Float64Array;
}

export type WireCallbacks = {
  // A param changed and was applied to the host; rebuild the mesh.
  onChange: () => void;
};

// Wire every data-param control inside `container` to `host`. Multi-instance
// safe: all lookups are scoped to `container`, so several forms can coexist.
// Re-render `container`'s innerHTML (from fresh form HTML) freely; the single
// delegated listener installed here keeps working.
export function wireParams(
  container: HTMLElement,
  host: ParamHost,
  cb: WireCallbacks,
) {
  const applyParam = (name: string, value: number) => {
    if (!Number.isFinite(value)) return;
    if (!host.set_param(name, value)) return;
    // Show/hide fields whose visibility depends on this param's value.
    for (const dep of container.querySelectorAll<HTMLElement>(
      `[data-visible-when="${name}"]`,
    )) {
      dep.hidden = Number(dep.dataset.visibleValue) !== value;
    }
    cb.onChange();
  };

  // Seed compound widget: a number box plus a dice button that re-rolls it.
  // Buttons emit no input event, so this needs its own listener; the legal
  // range comes from the box's max (Rust owns the bound).
  container.addEventListener("click", (e) => {
    const button = (e.target as HTMLElement).closest<HTMLElement>(
      "button.seed-dice",
    );
    if (!button) return;
    const name = button.dataset.param;
    if (!name) return;
    const box = container.querySelector<HTMLInputElement>(
      `input[data-param="${name}"]`,
    );
    if (!box) return;
    const seed = Math.floor(Math.random() * (Number(box.max) + 1));
    box.value = String(seed);
    applyParam(name, seed);
  });

  container.addEventListener("input", (e) => {
    const el = e.target as HTMLElement;
    const name = el.dataset.param;
    if (!name) return;

    // Base-size compound widget: a preset dropdown plus a "Custom" number box
    // that only shows for the "Custom" option. The two share a data-param but
    // don't twin-sync (the dropdown stays on "Custom" while the box drives the
    // value), so handle them explicitly.
    if (el.classList.contains("base-size-select")) {
      const select = el as HTMLSelectElement;
      const box = container.querySelector<HTMLInputElement>(
        `input.base-size-custom[data-param="${name}"]`,
      )!;
      const custom = select.value === "custom";
      box.hidden = !custom;
      // Seed the box with the chosen preset so "Custom" starts from it.
      if (!custom) box.value = select.value;
      applyParam(name, Number(custom ? box.value : select.value));
      return;
    }
    if (el.classList.contains("base-size-custom")) {
      applyParam(name, Number((el as HTMLInputElement).value));
      return;
    }

    // Unit-slider compound widget: a slider + number box (class unit-value)
    // driving a value, plus an mm/inches dropdown (class unit-select). The
    // stored parameter value is ALWAYS mm; the unit is display-only. The three
    // share a data-param but the select must never be twin-synced (setting a
    // <select> value to a number blanks it), so handle them explicitly here
    // before the generic path.
    if (el.classList.contains("unit-select")) {
      // Display-only unit switch: no param change, no mesh rebuild. Rescale the
      // twin inputs' min/max/step and shown value from the authoritative mm
      // value.
      const select = el as HTMLSelectElement;
      const names = [...host.param_names()];
      const mm = [...host.param_values()][names.indexOf(name)];
      const inches = select.value === "in";
      const shown = inches ? mm / 25.4 : mm;
      for (const input of container.querySelectorAll<HTMLInputElement>(
        `input[data-param="${name}"]`,
      )) {
        input.min = select.dataset[inches ? "inMin" : "mmMin"]!;
        input.max = select.dataset[inches ? "inMax" : "mmMax"]!;
        input.step = select.dataset[inches ? "inStep" : "mmStep"]!;
        input.value = String(Number(shown.toFixed(4)));
      }
      return;
    }
    if (el.classList.contains("unit-value")) {
      // Slider or number box: the shown value is in the currently selected
      // unit; convert to mm before storing.
      const input = el as HTMLInputElement;
      const select = container.querySelector<HTMLSelectElement>(
        `select.unit-select[data-param="${name}"]`,
      );
      const inches = select != null && select.value === "in";
      const shown = Number(input.value);
      if (!Number.isFinite(shown)) return;
      // Sync the twin inputs (but not the select).
      for (const twin of container.querySelectorAll<HTMLInputElement>(
        `input[data-param="${name}"]`,
      )) {
        if (twin !== el) twin.value = input.value;
      }
      applyParam(name, inches ? shown * 25.4 : shown);
      return;
    }

    const input = el as HTMLInputElement;
    const value =
      input.type === "checkbox" ? (input.checked ? 1 : 0) : Number(input.value);
    if (!Number.isFinite(value)) return;
    // Keep twin controls (e.g. a slider and its number box) in sync.
    for (const twin of container.querySelectorAll<HTMLInputElement>(
      `[data-param="${name}"]`,
    )) {
      if (twin !== el) twin.value = input.value;
    }
    applyParam(name, value);
  });
}

// Applies a name->value map to a host, which ignores any name it doesn't have:
// a scene carries the params of the build that saved it, and those may since
// have been renamed or dropped. Params the map omits keep the host's value.
export function applyParams(host: ParamHost, params: Record<string, number>) {
  for (const [name, value] of Object.entries(params)) {
    host.set_param(name, value);
  }
}

// `wireParams` captures its host, but a form gets replaced wholesale (a restore
// builds a fresh one; choosing a topper swaps its archetype). Wiring through a
// proxy that dereferences whichever form is current keeps one set of listeners
// over any number of forms; a null form (no topper chosen) accepts nothing.
export function formProxy(current: () => ParamsForm | null): ParamHost {
  return {
    set_param: (name, value) => current()?.set_param(name, value) ?? false,
    param_names: () => current()?.param_names() ?? [],
    param_values: () => current()?.param_values() ?? new Float64Array(),
  };
}

// Reads a host's params into a name->value map (for scene JSON building).
export function paramMap(host: ParamHost): Record<string, number> {
  const names = [...host.param_names()];
  const values = [...host.param_values()];
  const params: Record<string, number> = {};
  names.forEach((name, i) => (params[name] = values[i]));
  return params;
}

export type FormCallbacks = {
  // Rebuild the mesh; `refit` reframes the camera (archetype switch / base
  // size change).
  requestMesh: (refit: boolean) => void;
};

// A single archetype standing on its own, as scene JSON.
export type StandaloneScene = {
  kind: "standalone";
  archetypeId: number;
  params: Record<string, number>;
};

// This form only builds movement trays, though the scene format allows any
// standalone archetype.
const TRAY_ARCHETYPE_ID = 1;

// The standalone form: a single `ParamsForm` pinned to the movement tray,
// feeding a `{"kind":"standalone",...}` scene.
export class Form {
  private form: ParamsForm;
  private paramsDiv: HTMLElement;
  private cb: FormCallbacks;

  constructor(
    paramsDiv: HTMLElement,
    cb: FormCallbacks,
  ) {
    this.form = new ParamsForm(FormKind.MovementTray);
    this.paramsDiv = paramsDiv;
    this.cb = cb;

    paramsDiv.innerHTML = this.form.form_html();

    wireParams(paramsDiv, formProxy(() => this.form), {
      onChange: () => this.cb.requestMesh(false),
    });
  }

  // Randomly reassign every parameter of the current archetype, re-render the
  // form (visible_when hiding comes for free from the fresh HTML), and rebuild
  // the mesh. Sizes may change drastically, so reframe the camera.
  randomise() {
    this.form.randomize(Math.random() * 2 ** 53);
    this.paramsDiv.innerHTML = this.form.form_html();
    this.cb.requestMesh(true);
  }

  // Restore the form from a scene (a cart item or a shared string). A scene
  // naming any archetype but the movement tray is thrown out before anything is
  // touched. The form is rebuilt from scratch, so a param the scene doesn't
  // mention comes back at its default rather than keeping the value the user
  // happened to have; re-rendering from fresh HTML shows the result. The caller
  // reframes: this leaves the mesh alone.
  loadScene(scene: StandaloneScene) {
    if (scene.archetypeId !== TRAY_ARCHETYPE_ID) {
      throw new Error(
        `scene is for archetype ${scene.archetypeId}, not a movement tray`,
      );
    }
    this.form = new ParamsForm(FormKind.MovementTray);
    applyParams(this.form, scene.params);
    this.paramsDiv.innerHTML = this.form.form_html();
  }

  // The current archetype as a standalone scene JSON.
  sceneJson(): string {
    const scene: StandaloneScene = {
      kind: "standalone",
      archetypeId: TRAY_ARCHETYPE_ID,
      params: paramMap(this.form),
    };
    return JSON.stringify(scene);
  }
}
